import React, { useState, useEffect } from 'react';
import { useMiniERP } from './hooks/useMiniERP';
import { useStatusBanner, StatusBanner } from './components/MiniERPStatusBanner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faSearch } from '@fortawesome/free-solid-svg-icons';

const transactionTypes = ['Purchase', 'Sales', 'SampleOut', 'Damage'];

const MiniERPStockTransaction = ({ authState }) => {
  const {
    fetchStockTransactions,
    createStockTransaction,
    updateStockTransaction,
    fetchInventory,
  } = useMiniERP();

  const { status, showSuccess, showError } = useStatusBanner();

  const [transactionsList, setTransactionsList] = useState([]);
  const [inventoryList, setInventoryList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [editingTx, setEditingTx] = useState(null);

  const [formData, setFormData] = useState({
    type: 'Purchase',
    inventoryId: '',
    qty: 0,
    rate: 0,
    amount: 0,
    narration: '',
    date: new Date().toISOString().split('T')[0]
  });

  const loadAll = async () => {
    const [txRes, invRes] = await Promise.all([fetchStockTransactions(), fetchInventory()]);
    setTransactionsList(txRes?.data || []);
    setInventoryList(invRes?.data || []);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filteredTransactions = transactionsList.filter(t => {
    const matchesSearch = t.narration?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'All' || t.type === selectedType;
    return matchesSearch && matchesType;
  });

  const calculateAmount = (qty, rate) => qty * rate;

  const handleQtyRateChange = (field, value) => {
    const newForm = { ...formData, [field]: Number(value) || 0 };
    if (field === 'qty' || field === 'rate') {
      newForm.amount = calculateAmount(newForm.qty, newForm.rate);
    }
    setFormData(newForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.inventoryId || !formData.qty) {
      showError('Please select item and quantity.');
      return;
    }

    try {
      if (editingTx) {
        await updateStockTransaction(editingTx._id, formData);
        showSuccess('Stock transaction updated successfully!');
      } else {
        await createStockTransaction(formData);
        showSuccess('Stock transaction recorded successfully!');
      }
      await loadAll(); // refresh both the register and item stock balances
      setTimeout(() => { setShowModal(false); resetForm(); }, 700);
    } catch (err) {
      showError(err.message);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (tx) => {
    setEditingTx(tx);
    setFormData({
      type: tx.type,
      inventoryId: tx.inventoryId?._id || tx.inventoryId,
      qty: tx.qty,
      rate: tx.rate,
      amount: tx.amount,
      narration: tx.narration || '',
      date: new Date(tx.date).toISOString().split('T')[0]
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      type: 'Purchase',
      inventoryId: '',
      qty: 0,
      rate: 0,
      amount: 0,
      narration: '',
      date: new Date().toISOString().split('T')[0]
    });
    setEditingTx(null);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
          <div className="text-left">
            <h1 className="text-xl lg:text-3xl font-bold text-cyan-200">Stock In / Out</h1>
            <p className="text-xs lg:text-sm text-gray-400">Purchase • Sales • Sample Out • Damage</p>
          </div>
          <button
            onClick={openCreateModal}
            className="bg-cyan-600 hover:bg-cyan-500 w-fit px-2 py-1 rounded-2xl flex items-center gap-2 font-medium"
          >
            <FontAwesomeIcon icon={faPlus} /> New Transaction
          </button>
        </div>

        {/* Filters */}
        <div className="bg-gray-800 rounded-2xl p-4 mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-3.5 text-gray-500" />
            <input
              type="text"
              placeholder="Search narration..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 pl-11 py-3 rounded-xl"
            />
          </div>

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="bg-gray-700 border border-gray-600 px-2 py-1 rounded-xl"
          >
            <option value="All">All Types</option>
            {transactionTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        {/* Transactions List */}
        <div className="bg-gray-800 rounded-3xl overflow-hidden">
          {/* ================= MOBILE CARDS ================= */}
          <div className="block lg:hidden space-y-3 p-3">
            {filteredTransactions.map((tx) => (
              <div key={tx._id} className="bg-gray-800 border border-gray-700 rounded-xl p-3 text-xs">
                <div className="flex justify-between items-start">
                  <div>
                    <span className={`px-2 py-1 rounded-full text-[11px] font-medium ${
                      tx.type === 'Purchase' || tx.type === 'Sales'
                        ? 'bg-emerald-900 text-emerald-300'
                        : 'bg-rose-900 text-rose-300'
                    }`}>
                      {tx.type}
                    </span>
                    <div className="text-sm font-semibold text-white mt-1">
                      {tx.inventoryId?.name || 'Unknown Item'}
                    </div>
                  </div>
                  <button
                    onClick={() => openEditModal(tx)}
                    className="w-fit px-1 py-1 text-lg text-gray-400 hover:text-cyan-400"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-3 text-gray-300">
                  <div>
                    <div className="text-gray-500">Qty</div>
                    <div className="font-semibold">{tx.qty}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Rate</div>
                    <div>₹{tx.rate}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-gray-500">Amount</div>
                    <div className="font-bold text-cyan-300">₹{tx.amount}</div>
                  </div>
                </div>
                <div className="text-gray-500 mt-2">{new Date(tx.date).toLocaleDateString()}</div>
                {tx.narration && <div className="text-gray-400 mt-1">{tx.narration}</div>}
              </div>
            ))}
          </div>

          {/* ================= DESKTOP TABLE ================= */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-xs lg:text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-4 px-6">Date</th>
                  <th className="text-left py-4 px-6">Type</th>
                  <th className="text-left py-4 px-6">Item</th>
                  <th className="text-center py-4 px-6">Qty</th>
                  <th className="text-right py-4 px-6">Rate</th>
                  <th className="text-right py-4 px-6">Amount</th>
                  <th className="text-left py-4 px-6">Narration</th>
                  <th className="text-center py-4 px-6">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredTransactions.map((tx) => (
                  <tr key={tx._id} className="hover:bg-gray-700/50">
                    <td className="py-4 px-6">{new Date(tx.date).toLocaleDateString()}</td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        tx.type === 'Purchase' || tx.type === 'Sales'
                          ? 'bg-emerald-900 text-emerald-300'
                          : 'bg-rose-900 text-rose-300'
                      }`}>
                        {tx.type}
                      </span>
                    </td>
                    <td className="py-4 px-6 font-medium">
                      {tx.inventoryId?.name || 'Unknown Item'}
                    </td>
                    <td className="py-4 px-6 text-center font-semibold">{tx.qty}</td>
                    <td className="py-4 px-6 text-right">₹{tx.rate}</td>
                    <td className="py-4 px-6 text-right font-bold text-cyan-300">₹{tx.amount}</td>
                    <td className="py-4 px-6 text-gray-400 max-w-xs truncate">
                      {tx.narration || '-'}
                    </td>
                    <td className="py-4 px-6 text-center">
                      <button
                        onClick={() => openEditModal(tx)}
                        className="w-fit px-2 py-1 text-lg text-gray-400 hover:text-cyan-400"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredTransactions.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              No stock transactions yet.
            </div>
          )}
        </div>
      </div>

      {/* Transaction Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 md:p-8">
            <h2 className="text-2xl font-bold text-cyan-200 mb-6 text-left">
              {editingTx ? 'Edit Stock Transaction' : 'New Stock Transaction'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-gray-400 mb-1">Transaction Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-2 py-1"
                >
                  {transactionTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-400 mb-1">Item</label>
                <select
                  value={formData.inventoryId}
                  onChange={(e) => setFormData({ ...formData, inventoryId: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-2 py-1"
                  required
                >
                  <option value="">Select Item</option>
                  {inventoryList.filter(i => i.isActive || i._id === formData.inventoryId).map(item => (
                    <option key={item._id} value={item._id}>
                      {item.itemCode} - {item.name} ({item.currentStock})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-1">Quantity</label>
                  <input
                    type="number"
                    value={formData.qty}
                    onChange={(e) => handleQtyRateChange('qty', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-1">Rate (₹)</label>
                  <input
                    type="number"
                    value={formData.rate}
                    onChange={(e) => handleQtyRateChange('rate', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 mb-1">Amount (₹)</label>
                <input
                  type="number"
                  value={formData.amount}
                  readOnly
                  className="w-full bg-gray-800 border border-gray-600 rounded-xl px-4 py-3 text-cyan-300 font-medium"
                />
              </div>

              <div>
                <label className="block text-gray-400 mb-1">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                />
              </div>

              <div>
                <label className="block text-gray-400 mb-1">Narration / Remarks</label>
                <textarea
                  value={formData.narration}
                  onChange={(e) => setFormData({ ...formData, narration: e.target.value })}
                  style={{ height: "100px" }}
                  className="w-full bg-gray-700 border border-gray-600 rounded-2xl px-4 py-3"
                  placeholder="Optional remarks..."
                />
              </div>

              <StatusBanner status={status} />

              {editingTx && (
                <p className="text-xs text-gray-500">
                  Editing this transaction will re-adjust the item's current stock by the difference between the old and new quantity.
                </p>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 w-fit px-2 py-1 border border-gray-600 rounded-2xl hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 w-fit px-2 py-1 bg-cyan-600 hover:bg-cyan-500 rounded-2xl font-medium"
                >
                  {editingTx ? 'Update Transaction' : 'Record Transaction'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiniERPStockTransaction;
